// SF22.1: the second renderer type registerProjectCharacters can bind a
// custom rig to - a real skinned mesh loaded from the project's own uploaded
// GLB, posed via the same applyPose contract every other renderer uses, kept
// entirely separate from createGenericSkeletonRenderer's bone-gizmo fallback
// (that one stays for flat bone-transform rigs with no imported mesh).
import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { disposeProp } from '../animation/GalleryStage';
import { applyPose, type CharacterRenderer, type CharacterRendererProps } from '../rendererRegistry';
import { findSkinnedMesh } from './importRig';

export interface LoadedRig {
  scene: THREE.Object3D;
  bones: THREE.Bone[];
}

/**
 * Load a project's imported, pre-rigged GLB and expose its scene + skeleton
 * bones. Extracted from ImportedSkeletonRig so the player-avatar custom-body
 * path (FirstPersonController.tsx) can reuse the exact same GLTFLoader/
 * dispose/findSkinnedMesh handling instead of duplicating it - behavior is
 * unchanged, this is a pure extraction.
 */
export function useImportedSkeletonRig(url: string): LoadedRig | null {
  const [rig, setRig] = useState<LoadedRig | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loadedScene: THREE.Object3D | null = null;
    // The one decision "is there a loaded scene this effect no longer owns,
    // and if so, dispose it" - shared by both places that can discover that
    // (a late GLTFLoader callback, and this effect's own cleanup below) so
    // there is exactly one dispose call site, and a run that finds `cancelled`
    // false - because it already disposed it, or the load never arrived, or
    // it already got disposed earlier - can never double-dispose. Mirrors
    // AutoRigPanel.tsx's own fix for the identical race.
    const disposeIfStale = () => {
      if (cancelled && loadedScene) {
        disposeProp(loadedScene);
        loadedScene = null;
      }
    };
    new GLTFLoader().load(
      url,
      (gltf) => {
        // Captured unconditionally, regardless of `cancelled` - a load that
        // completes after this effect was already cleaned up (unmount, or
        // switching rigs mid-load) still produces real, GPU-backed
        // geometry/materials that need disposing. Checking `cancelled`
        // before this assignment (as this used to) meant a late arrival
        // never got assigned here at all, so the cleanup below - which, for
        // an in-flight network load, has always already run by the time this
        // callback fires, and runs only once - had nothing to dispose, and
        // the load leaked silently. Calling `disposeIfStale()` right here,
        // immediately after assigning, is what actually catches that case.
        loadedScene = gltf.scene;
        disposeIfStale();
        if (cancelled) return;
        const mesh = findSkinnedMesh(gltf.scene);
        gltf.scene.traverse((node) => {
          const m = node as THREE.Mesh;
          if (!m.isMesh) return;
          m.castShadow = true;
          m.receiveShadow = true;
        });
        setRig({ scene: gltf.scene, bones: mesh ? mesh.skeleton.bones : [] });
      },
      undefined,
      (err) => console.warn('[rig] imported skeleton renderer failed to load:', err),
    );
    return () => {
      cancelled = true;
      setRig(null);
      disposeIfStale();
    };
  }, [url]);

  return rig;
}

function ImportedSkeletonRig({ url, pose }: { url: string } & CharacterRendererProps) {
  const rig = useImportedSkeletonRig(url);

  useEffect(() => {
    if (!rig) return;
    for (const bone of rig.bones) applyPose(bone, pose, bone.name);
  }, [rig, pose]);

  return rig ? <primitive object={rig.scene} /> : null;
}

/**
 * A real skinned-mesh renderer for a project's own imported, pre-rigged GLB -
 * `registerProjectCharacters` binds one of these per `CharacterDefinition`
 * that carries an `importedGlbUrl`, instead of the generic per-bone gizmo.
 * Poses apply directly to the mesh's real skeleton bones by name, the same
 * `applyPose` contract every renderer uses, so the existing posing UI (bone
 * rotation sliders, saved poses) works unchanged against a real mesh.
 */
export function createImportedSkeletonRenderer(url: string): CharacterRenderer {
  function ImportedSkeletonRenderer({ pose }: CharacterRendererProps) {
    return <ImportedSkeletonRig url={url} pose={pose} />;
  }
  return ImportedSkeletonRenderer;
}
